import React, { ReactElement, useCallback, useContext, useState } from 'react';

import { ConversationStatus } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { AIChatSession } from 'src/components/AI/AIChatSession';
import { AILandingPage } from 'src/components/AI/AILandingPage';
import { AgentContext } from 'src/contexts/AgentContext';

import { Message } from '../types';

export interface AIChatMainAreaProps {
    conversationId: string | null;
    messages: Message[];
    onSendMessage: (message: string, endpoint: string, toolkits: string[] | undefined) => Promise<void>;
    isLoading: boolean;
    isNewChat: boolean;
    isAIResponding?: boolean;
    isDisabled?: boolean;
    conversationStatus?: ConversationStatus;
}

export const AIChatMainArea: React.FC<AIChatMainAreaProps> = ({
    conversationId,
    messages,
    onSendMessage,
    isLoading,
    isNewChat,
    isAIResponding = false,
    isDisabled = false,
    conversationStatus = ConversationStatus.READY
}) => {
    const [isSending, setIsSending] = useState(false);
    const { endpoint, selectedToolkits: toolkits } = useContext(AgentContext);

    const handleSendMessage = useCallback(
        async (inputMessage: string) => {
            if (!inputMessage.trim() || isSending || isDisabled) return;

            const messageToSend = inputMessage.trim();
            setIsSending(true);

            try {
                await onSendMessage(messageToSend, endpoint, toolkits);
            } finally {
                setIsSending(false);
            }
        },
        [endpoint, onSendMessage, toolkits]
    );

    let innerComponent: ReactElement;

    if (!conversationId) {
        innerComponent = (
            <AILandingPage
                conversationStatus={conversationStatus}
                handleSendMessage={handleSendMessage}
                isSending={isSending}
                isDisabled={isDisabled}
            />
        );
    } else {
        innerComponent = (
            <AIChatSession
                conversationId={conversationId}
                conversationStatus={conversationStatus}
                messages={messages}
                handleSendMessage={handleSendMessage}
                isSending={isSending}
                isDisabled={isDisabled}
                isAIResponding={isAIResponding}
                isLoading={isLoading}
            />
        );
    }

    return innerComponent;
};
